import { useCallback, useEffect, useRef, useMemo } from 'react'

const noop = () => undefined

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  flushOnUnmount?: boolean,
) {
  const handleCallback = useCallbackRef(callback)
  const debounceTimerRef = useRef(0)

  const lastCallback = Object.assign(
    useCallback(
      (...args: Parameters<T>) => {
        window.clearTimeout(debounceTimerRef.current)
        const flush = () => {
          if (debounceTimerRef.current !== 0) {
            debounceTimerRef.current = 0
            handleCallback(...args)
          }
        }
        lastCallback.flush = flush
        debounceTimerRef.current = window.setTimeout(flush, delay)
      },
      [handleCallback, delay],
    ),
    { flush: noop },
  )

  useEffect(
    () => () => {
      window.clearTimeout(debounceTimerRef.current)
      if (flushOnUnmount) {
        lastCallback.flush()
      }
    },
    [lastCallback, flushOnUnmount],
  )

  return lastCallback
}

// локальный хелпер, но если понадобится можно вынести вверх
function useCallbackRef<T extends (...args: any[]) => any>(callback: T | undefined): T {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  })

  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, [])
}
